import "./App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/login";
import NoAccess from "./pages/NoAccess";
import Loader from "./components/common/Loader";
import { useAccess, AccessProvider } from "./services/auth/AccessProvider";
import { hasAuthSession } from "./services/auth/authSession";
import ManageUser from "./pages/ManageUser";
import CourseManagement from "./pages/CourseManagement";
import CourseCategories from "./pages/CourseCategories";
import CourseDetails from "./pages/CourseDetails";
import DeletionHistory from "./pages/DeletionHistory";
import StudentProgress from "./pages/StudentProgress";
import StudentOverView from "./pages/StudentOverView";
import StudentProfile from "./pages/StudentProfile";
import InstructorProfile from "./pages/InstructorProfile";
import ActivityLog from "./pages/ActivityLog";
import ManagePurchase from "./pages/ManagePurchase";
import ManageRequest from "./pages/ManageRequest";
import RequestDemoSession from "./pages/RequestDemoSession";
import Certificates from "./pages/Certificates";
import ToastContainer from "./components/common/ToastContainer";

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { profile, isResolving, canAccess, landingPath } = useAccess();

  if (!hasAuthSession()) {
    return <Navigate to="/" replace />;
  }

  if (isResolving || !profile) {
    return (
      <div className="flex min-h-screen items-center justify-center text-[#923D8C]">
        <Loader size="xl" />
      </div>
    );
  }

  if (
    location.pathname === "/admin/no-access" &&
    landingPath !== "/admin/no-access"
  ) {
    return <Navigate to={landingPath} replace />;
  }

  if (!canAccess(location.pathname)) {
    return <Navigate to={landingPath} replace />;
  }

  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { profile, isResolving, landingPath } = useAccess();

  if (!hasAuthSession()) {
    return children;
  }

  if (isResolving || !profile) {
    return (
      <div className="flex min-h-screen items-center justify-center text-[#923D8C]">
        <Loader size="xl" />
      </div>
    );
  }

  return <Navigate to={landingPath} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AccessProvider>
      <ToastContainer />
      <Routes>
        <Route
          path="/"
          element={
            <PublicOnlyRoute>
              <Login />
            </PublicOnlyRoute>
          }
        />

        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
         <Route
          path="/admin/manage-user"
          element={
            <ProtectedRoute>
              <ManageUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/course-management"
          element={
            <ProtectedRoute>
              <CourseManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/course-management/course-detail/:courseId"
          element={
            <ProtectedRoute>
              <CourseDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/course-management/view-category"
          element={
            <ProtectedRoute>
              <CourseCategories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/deletion-history"
          element={
            <ProtectedRoute>
              <DeletionHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/activity-log"
          element={
            <ProtectedRoute>
              <ActivityLog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/purchases"
          element={
            <ProtectedRoute>
              <ManagePurchase />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests"
          element={
            <ProtectedRoute>
              <ManageRequest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/request/demo-session/:requestId"
          element={
            <ProtectedRoute>
              <RequestDemoSession />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/student-progress"
          element={
            <ProtectedRoute>
              <StudentProgress />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/student-progress/student-overview/:studentId"
          element={
            <ProtectedRoute>
              <StudentOverView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/manage-user/student-profile/:studentId"
          element={
            <ProtectedRoute>
              <StudentProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/manage-user/instructor-profile/:instructorId"
          element={
            <ProtectedRoute>
              <InstructorProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/certificates"
          element={
            <ProtectedRoute>
              <Certificates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/no-access"
          element={
            <ProtectedRoute>
              <NoAccess />
            </ProtectedRoute>
          }
        />
      </Routes>
      </AccessProvider>
    </BrowserRouter>
  );
}

export default App;
